import { createSelector } from "reselect";
import { RootState } from "../../reducers";
import {
  Cluster,
  ClusterSetting,
  ClusterTemplate,
  Nodegroup,
  TableCluster
} from "./types";

export interface TableNodegroup extends Nodegroup {
  flavorName: string;
}

export const clusterSelector = (state: RootState): Cluster | null =>
  state.clusters.cluster;

export const tableClustersSelector = (
  state: RootState
): TableCluster[] | null =>
  state.clusters.clusters?.map((cluster) => {
    const masterFlavor = (state.instances.flavors || []).find(
      (flavor) => flavor.id === cluster.master_flavor_id
    );

    const nodeFlavor = (state.instances.flavors || []).find(
      (flavor) => flavor.id === cluster.flavor_id
    );

    const totalvCPUs =
      (masterFlavor?.vcpus || 0) * cluster.master_count +
      (nodeFlavor?.vcpus || 0) * cluster.node_count;

    const totalMemory =
      ((masterFlavor?.ram || 0) * cluster.master_count +
        (nodeFlavor?.ram || 0) * cluster.node_count) /
      1024;

    return {
      name: cluster.name,
      status: cluster.status,
      id: cluster.id,
      masterCount: cluster.master_count,
      nodeCount: cluster.node_count,
      clusterTemplateId: cluster.cluster_template_id,
      coeVersion: cluster.coe_version || cluster.labels.kube_tag,
      kubeVersion: cluster.labels.kube_version,
      kubeTag: cluster.labels.kube_tag,
      totalvCPUs: totalvCPUs,
      totalMemory: `${totalMemory} GiB`
    };
  }) || null;

export const isClusterCreatingSelector = (state: RootState): boolean =>
  state.clusters.isClusterCreating;

export const isClusterResizingSelector = (state: RootState): boolean =>
  state.clusters.isClusterResizing;

export const isClusterDeletingSelector = (state: RootState): boolean =>
  state.clusters.isClusterDeleting;

export const areClustersLoadingSelector = (state: RootState): boolean =>
  state.clusters.areClustersLoading;

export const clusterTemplatesSelector = (
  state: RootState
): ClusterTemplate[] | null => state.clusters.clusterTemplates;

export const areClusterTemplatesLoadingSelector = (state: RootState): boolean =>
  state.clusters.areClusterTemplatesLoading;

export const isClusterUpgradingSelector = (state: RootState): boolean =>
  state.clusters.isClusterUpgrading;

export const isClusterCARotatingSelector = (state: RootState): boolean =>
  state.clusters.isClusterCARotating;

export const isAnyClusterOperationInProgressSelector = (
  state: RootState
): boolean =>
  state.clusters.isClusterCreating ||
  state.clusters.isClusterResizing ||
  state.clusters.isClusterDeleting ||
  state.clusters.isClusterUpgrading ||
  state.clusters.isClusterCARotating;

// Nodegroup Selectors
export const nodegroupsSelector = (state: RootState): Nodegroup[] | null =>
  state.clusters.nodegroups;

export const tableNodegroupsSelector = createSelector(
  [
    (state: RootState) => state.clusters.nodegroups,
    (state: RootState) => state.instances.flavors
  ],
  (nodegroups, flavors): TableNodegroup[] | null =>
    nodegroups?.map((nodegroup) => {
      const flavor = (flavors || []).find((f) => f.id === nodegroup.flavor_id);
      const flavorName = flavor?.name || nodegroup.flavor_id || "N/A";

      return {
        ...nodegroup,
        flavorName
      };
    }) || null
);

export const areNodegroupsLoadingSelector = (state: RootState): boolean =>
  state.clusters.areNodegroupsLoading;

// --- START: Selectors for selected nodegroup details ---
export const selectedNodegroupDetailsSelector = (
  state: RootState
): Nodegroup | null => state.clusters.selectedNodegroupDetails;

export const isNodegroupDetailsLoadingSelector = (state: RootState): boolean =>
  state.clusters.isNodegroupDetailsLoading;
// --- END: Selectors for selected nodegroup details ---

export const isNodegroupCreatingSelector = (state: RootState): boolean =>
  state.clusters.isNodegroupCreating;

export const isNodegroupUpdatingSelector = (state: RootState): boolean =>
  state.clusters.isNodegroupUpdating;

export const isNodegroupDeletingSelector = (state: RootState): boolean =>
  state.clusters.isNodegroupDeleting;

export const isAnyNodegroupOperationInProgressSelector = (
  state: RootState
): boolean =>
  state.clusters.isNodegroupCreating ||
  state.clusters.isNodegroupUpdating ||
  state.clusters.isNodegroupDeleting;

export const clusterSettingsSelector = (
  state: RootState
): ClusterSetting[] | null => state.clusters.clusterSettings;

export const clusterTemplateSettingsSelector = (
  state: RootState
): ClusterSetting[] | null => state.clusters.clusterTemplateSettings;

export const areClusterSettingsLoadingSelector = (state: RootState): boolean =>
  state.clusters.areClusterSettingsLoading;

export const areClusterTemplateSettingsLoadingSelector = (
  state: RootState
): boolean => state.clusters.areClusterTemplateSettingsLoading;

export const isClusterSettingsUpdatingSelector = (state: RootState): boolean =>
  state.clusters.isClusterSettingsUpdating;
